/**
 * One setting's value, in the words its row uses for it.
 *
 * Its own module, and a plain one, because three surfaces need the same answer
 * and none of them should have to draw the others: the workspace's rows show
 * what a value inherits, the publish diff shows what a value was and becomes,
 * and the reach names what a workspace is about to stop getting. A second
 * spelling of `16384` — `16384`, `16 GB`, `"16384"` — would be a second thing
 * to learn per surface.
 *
 * **Never raw JSON.** `agentUpdate: "pin"` means nothing to the person who
 * chose *Keep the release in the image*, and a dialog that
 * asks them to confirm `"pin"` is asking them to confirm a string.
 */
import { EFFORT_LABEL, MODEL_LABEL, type SettingSpec } from './org-catalogue';
import { extensionsSummary, normalizeExtensions } from '@airlock/contracts/settings/extensions';
import { autoLand, autoLandSummary } from '@airlock/contracts/settings/auto-land';
import { audit, auditSummary } from '@airlock/contracts/settings/audit';
import { DEFAULT_SETTINGS } from '@airlock/contracts/settings/defaults';

export type Values = Record<string, unknown>;

/**
 * What the row shows for `values[spec.key]`.
 *
 * The whole record is taken rather than one value because one row is two keys:
 * the model and the effort are picked as a pair, and reading the model alone
 * would print half of it.
 */
export function showValue(spec: SettingSpec, values: Values): string {
  const value = values[spec.key];
  if (value === undefined || value === null) return '—';
  if (spec.kind === 'modelEffort') {
    const model = String(values.defaultModel ?? '');
    const effort = values.defaultEffort ? String(values.defaultEffort) : null;
    const named = MODEL_LABEL[model] ?? model;
    return effort ? `${named} · ${EFFORT_LABEL[effort] ?? effort}` : named;
  }
  if (spec.kind === 'bool') return value ? 'On' : 'Off';
  if (spec.kind === 'agents') return (Array.isArray(value) ? value : []).join(' · ') || 'none';
  if (spec.kind === 'oneOf') return spec.options?.find((option) => option.value === value)?.label ?? String(value);
  if (spec.kind === 'int') {
    // Shown in the unit it is asked in: `idleHintMs` is typed in seconds and
    // stored in milliseconds, and a diff that said 20000 → 45000 would be
    // reporting a change nobody made in those terms.
    const shown = spec.scale ? Math.round(Number(value) / spec.scale) : Number(value);
    return spec.unit ? `${shown} ${spec.unit}` : String(shown);
  }
  // A document has no one-line value, so the row names it rather than printing
  // it: a Dockerfile in a 288px column is how these ended up as JSON.
  if (spec.kind === 'wide') return String(value).split('\n')[0] || 'an image of its own';
  // The other document (§5.26). What a diff, a reach or an inherited row wants
  // to know about it is *how much of it there is*, which is the one thing three
  // lists of records can say in a line.
  if (spec.kind === 'extensions') return extensionsSummary(normalizeExtensions(value));
  // Five switches in one setting: the steps that run, in their order.
  if (spec.kind === 'autoLand') return autoLandSummary(autoLand(value, DEFAULT_SETTINGS.autoLand));
  // Three values in one: which findings go back, and what draws the map.
  if (spec.kind === 'audit') return auditSummary(audit(value, DEFAULT_SETTINGS.audit));
  return String(value) || '—';
}
